import React, { useState } from 'react';
import api from '../services/api';
import './BugReportForm.css';

const BugReportForm = ({ onClose }) => {
  const [formType, setFormType] = useState('bug');
  const [bugTitle, setBugTitle] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [whatDoingBefore, setWhatDoingBefore] = useState('');
  const [whatResulted, setWhatResulted] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');

  // Feature request fields
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Calculate total character count for bug reports
  const getTotalCharacters = () => {
    return whatHappened.length + whatDoingBefore.length + whatResulted.length;
  };

  const isFormValid = () => {
    if (formType === 'bug') {
      const hasRequiredFields =
        bugTitle.trim() !== '' &&
        whatHappened.length >= 30 &&
        whatDoingBefore.length >= 30 &&
        whatResulted.length >= 30;

      const meetsMinimum = getTotalCharacters() >= 250;

      return hasRequiredFields && meetsMinimum;
    } else {
      return featureTitle.trim() !== '' && featureDescription.trim() !== '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        type: formType,
        page: window.location.pathname,
        user_agent: window.navigator.userAgent,
        timestamp: new Date().toISOString(),
      };

      if (formType === 'bug') {
        payload.bug_title = bugTitle;
        payload.what_happened = whatHappened;
        payload.what_doing_before = whatDoingBefore;
        payload.what_resulted = whatResulted;
        payload.steps_to_reproduce = stepsToReproduce;
        payload.expected_behavior = expectedBehavior;
      } else {
        payload.feature_title = featureTitle;
        payload.feature_description = featureDescription;
      }

      const response = await api.post('/feedback/submit', payload);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to submit feedback');
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bug-report-modal-overlay" onClick={onClose}>
        <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-message">
            <h2>Thank You!</h2>
            <p>Your {formType === 'bug' ? 'bug report' : 'feature request'} has been submitted successfully.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bug-report-modal-overlay" onClick={onClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bug-report-header">
          <h2>Report a Bug or Request a Feature</h2>
          <button className="close-button" onClick={onClose} type="button">&times;</button>
        </div>

        <p className="bug-report-subtitle">Select the type and provide comprehensive details</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="formType">Type</label>
            <select
              id="formType"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              className="form-select"
            >
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
            </select>
          </div>

          {formType === 'bug' ? (
            <>
              <div className="form-group">
                <label htmlFor="bugTitle">Bug Title *</label>
                <input
                  type="text"
                  id="bugTitle"
                  className="form-input"
                  placeholder="Brief title describing the bug"
                  value={bugTitle}
                  onChange={(e) => setBugTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="whatHappened">
                  What Happened? * <span className="min-chars">(minimum 30 characters)</span>
                </label>
                <textarea
                  id="whatHappened"
                  className="form-textarea"
                  placeholder="Describe exactly what happened when the bug occurred..."
                  value={whatHappened}
                  onChange={(e) => setWhatHappened(e.target.value)}
                  rows="4"
                  required
                />
                <div className="char-count">{whatHappened.length} characters</div>
              </div>

              <div className="form-group">
                <label htmlFor="whatDoingBefore">
                  What Were You Doing Before/When It Happened? * <span className="min-chars">(minimum 30 characters)</span>
                </label>
                <textarea
                  id="whatDoingBefore"
                  className="form-textarea"
                  placeholder="Describe what you were doing before or when the bug occurred..."
                  value={whatDoingBefore}
                  onChange={(e) => setWhatDoingBefore(e.target.value)}
                  rows="4"
                  required
                />
                <div className="char-count">{whatDoingBefore.length} characters</div>
              </div>

              <div className="form-group">
                <label htmlFor="whatResulted">
                  What Resulted After This? * <span className="min-chars">(minimum 30 characters)</span>
                </label>
                <textarea
                  id="whatResulted"
                  className="form-textarea"
                  placeholder="Describe what happened as a result of this bug..."
                  value={whatResulted}
                  onChange={(e) => setWhatResulted(e.target.value)}
                  rows="4"
                  required
                />
                <div className="char-count">{whatResulted.length} characters</div>
              </div>

              <div className="form-group">
                <label htmlFor="stepsToReproduce">
                  Steps to Reproduce <span className="optional">(optional)</span>
                </label>
                <textarea
                  id="stepsToReproduce"
                  className="form-textarea"
                  placeholder="List the steps to reproduce this bug (e.g., 1. Go to... 2. Click on... 3. See error)"
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  rows="4"
                />
                <div className="char-count">{stepsToReproduce.length} characters</div>
              </div>

              <div className="form-group">
                <label htmlFor="expectedBehavior">
                  Expected Behavior <span className="optional">(optional)</span>
                </label>
                <textarea
                  id="expectedBehavior"
                  className="form-textarea"
                  placeholder="What did you expect to happen instead?"
                  value={expectedBehavior}
                  onChange={(e) => setExpectedBehavior(e.target.value)}
                  rows="3"
                />
                <div className="char-count">{expectedBehavior.length} characters</div>
              </div>

              <div className="total-char-count">
                Total: {getTotalCharacters()} / 250 characters (minimum)
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="featureTitle">Feature Title / Name *</label>
                <input
                  type="text"
                  id="featureTitle"
                  className="form-input"
                  placeholder="Brief name or title for the feature"
                  value={featureTitle}
                  onChange={(e) => setFeatureTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="featureDescription">Description *</label>
                <textarea
                  id="featureDescription"
                  className="form-textarea"
                  placeholder="Describe the feature you would like to see..."
                  value={featureDescription}
                  onChange={(e) => setFeatureDescription(e.target.value)}
                  rows="8"
                  required
                />
                <div className="char-count">{featureDescription.length} characters</div>
              </div>
            </>
          )}

          {submitError && (
            <div className="error-message">{submitError}</div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : `Submit ${formType === 'bug' ? 'Bug Report' : 'Feature Request'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReportForm;
